/**
 * RecordHistoryModal — Kayıt Bazlı İşlem Geçmişi (Entity Audit Trail) modalı.
 *
 * Herhangi bir kaydın (fatura, hızlı satış ekranı, ...) üç nokta menüsünden açılır;
 * kim, ne zaman, hangi alanı eski değerden yeni değere değiştirdi zaman akışı olarak
 * gösterir. Veri backend'den sunucu taraflı sayfalamayla gelir (EntityChangeLogs).
 *
 * Parent eşleşmesi backend'dedir: örn. entityType='Invoice' için fatura kalemi
 * (InvoiceItem) değişiklikleri de aynı geçmişte listelenir.
 *
 * Alan adları ham İngilizce property adlarıdır; `fieldLabels` prop'u ile
 * Türkçeleştirilebilir (eşleşmeyen alanlar ham adıyla gösterilir).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  getEntityHistory,
  parseFieldChanges,
  type EntityChangeLogEntry,
} from '../../services/entityHistoryService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Backend entity CLR adı (örn. 'Invoice', 'QuickSaleScreen'). */
  entityType: string;
  /** Kaydın ID'si (fatura Guid, ekran int). */
  entityId: string | number | null;
  /** Modal başlığında gösterilecek kayıt adı (örn. fatura no, ekran adı). */
  recordLabel?: string;
  /** Alan adı → Türkçe etiket eşlemesi (eşleşmezse ham alan adı gösterilir). */
  fieldLabels?: Record<string, string>;
  /** Entity türü → Türkçe etiket (örn. { InvoiceItem: 'Fatura Kalemi' }). */
  entityTypeLabels?: Record<string, string>;
  /**
   * JSON alan farklarında anahtar → okunur ad eşlemesi (örn. personel uid → ad soyad).
   * AssignedPersonnelJson gibi sözlük alanlarında ham uid yerine isim gösterilir.
   */
  jsonKeyLabels?: Record<string, string>;
}

const CHANGE_TYPE_COLORS: Record<string, 'success' | 'info' | 'error'> = {
  Created: 'success',
  Updated: 'info',
  Deleted: 'error',
};

const ROLE_COLORS: Record<string, 'primary' | 'secondary' | 'warning' | 'default'> = {
  admin: 'primary',
  subadmin: 'secondary',
  manager: 'warning',
};

// Accordion başlığında kaydı tanıtan alan (öncelik sırasıyla ilk eşleşen kullanılır) —
// örn. fatura kaleminde ItemName, ekranda Name; Created'ta new, Deleted'ta old dolu olur.
const SUMMARY_LABEL_FIELDS = ['ItemName', 'Name', 'InvoiceNumber', 'Title', 'AccountName'];

const summaryLabel = (changes: { field: string; old: string | null; new: string | null }[]): string | null => {
  for (const field of SUMMARY_LABEL_FIELDS) {
    const change = changes.find((c) => c.field === field);
    const value = change?.new ?? change?.old;
    if (value) return value;
  }
  return null;
};

// ── JSON alan farkı yardımcıları ─────────────────────────────────────────────
// Bazı alanlar (AssignedPersonnelJson, PaymentOptionsJson...) DB'de JSON metni
// olarak tutulur; kayıt güncellenince tüm JSON eski/yeni olarak loglanır. Ham
// bloğu basmak yerine yalnız DEĞİŞEN anahtarlar okunur biçimde gösterilir.

/** Değer JSON nesnesi/dizisi ise parse eder, değilse undefined döner. */
const tryParseJson = (value: string | null): any | undefined => {
  if (!value) return undefined;
  const s = value.trim();
  if (!s.startsWith('{') && !s.startsWith('[')) return undefined;
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
};

interface JsonDiffRow {
  key: string;
  old: unknown;
  new: unknown;
}

/** Görülebilir azami JSON fark satırı (kalanı "+N" olarak özetlenir). */
const MAX_JSON_DIFF_ROWS = 30;

/**
 * İki düz JSON nesnesinin anahtar bazlı farkı. Boolean sözlüklerde (personel
 * atama haritası gibi) `false` ile "anahtar yok" aynı anlama gelir — bu tür
 * çiftler değişiklik sayılmaz ki yeni personel eklenince gürültü oluşmasın.
 */
/**
 * JSON dizisini anahtarlı nesneye çevirir (öğe adı/id'si varsa onunla, yoksa
 * satır numarasıyla) — böylece dizi alanlar da anahtar bazlı fark alabilir.
 */
const normalizeJsonForDiff = (value: any): any => {
  if (!Array.isArray(value)) return value;
  const obj: Record<string, unknown> = {};
  value.forEach((item, idx) => {
    const key =
      (item && typeof item === 'object' && (item.name || item.title || item.id)) || `#${idx + 1}`;
    obj[String(key)] = item;
  });
  return obj;
};

const jsonObjectDiff = (oldObj: any, newObj: any): JsonDiffRow[] => {
  const keys = new Set([...Object.keys(oldObj ?? {}), ...Object.keys(newObj ?? {})]);
  const rows: JsonDiffRow[] = [];
  for (const key of keys) {
    const ov = oldObj?.[key];
    const nv = newObj?.[key];
    if (JSON.stringify(ov) === JSON.stringify(nv)) continue;
    const falsy = (v: unknown) => v === undefined || v === false;
    if (falsy(ov) && falsy(nv)) continue;
    rows.push({ key, old: ov, new: nv });
  }
  return rows;
};

const RecordHistoryModal: React.FC<Props> = ({
  open,
  onClose,
  entityType,
  entityId,
  recordLabel,
  fieldLabels,
  entityTypeLabels,
  jsonKeyLabels,
}) => {
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState<EntityChangeLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI TablePagination 0 bazlı; backend 1 bazlı
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (entityId === null || entityId === undefined || entityId === '') return;
    setLoading(true);
    setError(null);
    try {
      const result = await getEntityHistory(entityType, entityId, page + 1, pageSize);
      setItems(result.items);
      setTotal(result.total);
    } catch (err: any) {
      console.error('[RecordHistoryModal] Geçmiş yüklenemedi:', err);
      setError(err?.response?.data?.message || err?.message || t('recordHistory.loadError'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, page, pageSize, t]);

  useEffect(() => {
    if (open) loadData();
  }, [open, loadData]);

  // Modal her açılışta ilk sayfadan başlasın.
  useEffect(() => {
    if (open) setPage(0);
  }, [open, entityType, entityId]);

  const formatDate = (iso: string) => {
    // Backend UTC yazar; yerel saate çevrilir.
    const date = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
    return date.toLocaleString(i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const changeTypeLabel = (changeType: string) => {
    switch (changeType) {
      case 'Created':
        return t('recordHistory.created');
      case 'Deleted':
        return t('recordHistory.deleted');
      default:
        return t('recordHistory.updated');
    }
  };

  const fieldLabel = (field: string) => fieldLabels?.[field] || field;

  const valueText = (value: string | null) => {
    if (value === null || value === '') return t('recordHistory.emptyValue');
    if (value === 'true') return t('recordHistory.yes');
    if (value === 'false') return t('recordHistory.no');
    return value;
  };

  /** JSON fark satırındaki tekil değeri okunur metne çevirir. */
  const jsonValueText = (value: unknown): string => {
    if (value === undefined || value === null || value === '') return t('recordHistory.emptyValue');
    if (value === true) return t('recordHistory.yes');
    if (value === false) return t('recordHistory.no');
    if (typeof value === 'object') {
      const s = JSON.stringify(value);
      return s.length > 120 ? `${s.slice(0, 120)}…` : s;
    }
    return String(value);
  };

  /**
   * Eski/yeni değer JSON nesnesiyse ham bloğu basmak yerine yalnız değişen
   * anahtarları "Ad: eski → yeni" satırları olarak gösterir.
   */
  const renderChangeValue = (change: { old: string | null; new: string | null }) => {
    const oldJson = normalizeJsonForDiff(tryParseJson(change.old));
    const newJson = normalizeJsonForDiff(tryParseJson(change.new));
    const isPlainObject = (v: any) => v === undefined || (typeof v === 'object' && v !== null);
    const bothObjects =
      isPlainObject(oldJson) && isPlainObject(newJson) && (oldJson !== undefined || newJson !== undefined);

    if (!bothObjects) return null;

    const rows = jsonObjectDiff(oldJson, newJson);
    if (rows.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          {t('recordHistory.noEffectiveChange')}
        </Typography>
      );
    }
    const visible = rows.slice(0, MAX_JSON_DIFF_ROWS);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {visible.map((row) => (
          <Typography key={row.key} variant="body2" sx={{ wordBreak: 'break-word' }}>
            <Box component="span" sx={{ fontWeight: 500 }}>
              {jsonKeyLabels?.[row.key] || row.key}
            </Box>
            {': '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {jsonValueText(row.old)}
            </Box>
            {' → '}
            {jsonValueText(row.new)}
          </Typography>
        ))}
        {rows.length > MAX_JSON_DIFF_ROWS && (
          <Typography variant="caption" color="text.secondary">
            {t('recordHistory.moreChanges', { count: rows.length - MAX_JSON_DIFF_ROWS })}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
        <HistoryIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="div" noWrap>
            {t('recordHistory.title')}
          </Typography>
          {recordLabel && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {recordLabel}
            </Typography>
          )}
        </Box>
        <IconButton onClick={loadData} disabled={loading} size="small" title={t('recordHistory.refresh')}>
          <RefreshIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 12, top: 14 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 320 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('recordHistory.empty')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((entry) => {
              const changes = parseFieldChanges(entry.changesJson);
              const isChildEntity = entry.entityType !== entityType;
              const hasChanges = changes.length > 0;
              const label = summaryLabel(changes);
              return (
                <Accordion
                  key={entry.id}
                  variant="outlined"
                  disableGutters
                  disabled={!hasChanges}
                  sx={{
                    '&:before': { display: 'none' },
                    '&.Mui-disabled': { bgcolor: 'transparent', opacity: 1 },
                  }}
                >
                  <AccordionSummary
                    expandIcon={hasChanges ? <ExpandMoreIcon /> : undefined}
                    sx={{ '& .MuiAccordionSummary-content': { minWidth: 0, my: 1 } }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
                      <Chip
                        size="small"
                        label={changeTypeLabel(entry.changeType)}
                        color={CHANGE_TYPE_COLORS[entry.changeType] || 'default'}
                      />
                      {isChildEntity && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={entityTypeLabels?.[entry.entityType] || entry.entityType}
                        />
                      )}
                      {label && (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 260, color: 'text.primary' }}
                          title={label}
                        >
                          {label}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {entry.actorName || t('recordHistory.system')}
                        </Typography>
                      </Box>
                      {entry.actorRole && entry.actorRole !== 'system' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={entry.actorRole}
                          color={ROLE_COLORS[entry.actorRole.toLowerCase()] || 'default'}
                        />
                      )}
                      {hasChanges && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {t('recordHistory.fieldCount', { count: changes.length })}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
                      >
                        {formatDate(entry.createdAt)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  {hasChanges && (
                    <AccordionDetails sx={{ pt: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: '30%' }}>{t('recordHistory.field')}</TableCell>
                              <TableCell sx={{ width: '35%' }}>{t('recordHistory.oldValue')}</TableCell>
                              <TableCell sx={{ width: '35%' }}>{t('recordHistory.newValue')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {changes.map((change, idx) => {
                              const jsonDiff = renderChangeValue(change);
                              return (
                                <TableRow key={`${entry.id}-${idx}`}>
                                  <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                                    {fieldLabel(change.field)}
                                  </TableCell>
                                  {jsonDiff ? (
                                    // JSON alan: ham blok yerine değişen anahtarlar tek hücrede.
                                    <TableCell colSpan={2}>{jsonDiff}</TableCell>
                                  ) : (
                                    <>
                                      <TableCell sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                                        {valueText(change.old)}
                                      </TableCell>
                                      <TableCell sx={{ wordBreak: 'break-word' }}>
                                        {valueText(change.new)}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('recordHistory.rowsPerPage')}
        />
        <Button onClick={onClose}>{t('recordHistory.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordHistoryModal;
